import { DateRange, TimeValue } from '@doss/common-all'
import dayjs, { Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { ArrowLeft, ArrowRight, CalendarIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDebounce } from 'react-use'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { cn } from '@/lib/utils'
import { Input } from '~/@/components/ui/input-v2'
import { ApplicationOrientation } from '~/common/generated/apollo-operations'
import { useApplicationOrientation } from '~/common/hooks/useApplicationOrientation'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { TimePicker } from '../time-picker/time-picker'

dayjs.extend(customParseFormat)

type DateRangeContentProps = {
  startDateString: string
  endDateString: string
  onRangeChange: (range: DateRange) => void
  dateFormat: string
  error: string | null
  setError: (error: string | null) => void
  disabled?: boolean
  onDoneClick: () => void
  currentMonth: Dayjs
  onMonthChange: (date: Date) => void
  dateRange?: DateRange
  /** When true, shows time inputs in addition to date inputs */
  isDateTime?: boolean
}

export function DateRangeContent({
  startDateString,
  endDateString,
  onRangeChange,
  dateFormat,
  disabled,
  setError,
  error,
  onDoneClick,
  currentMonth,
  onMonthChange,
  dateRange,
  isDateTime,
}: DateRangeContentProps) {
  const { orientation } = useApplicationOrientation()

  const { t } = useTypedTranslation()

  const [focused, setFocused] = useState<'start' | 'end' | null>(null)
  const [startInputValue, setStartInputValue] = useState(startDateString)
  const [endInputValue, setEndInputValue] = useState(endDateString)
  const startInputRef = useRef<HTMLInputElement>(null)
  const endInputRef = useRef<HTMLInputElement>(null)

  // Derive time values directly from dateRange prop (no sync useEffects needed)
  const startTimeValue: TimeValue = useMemo(
    () => ({
      hour: dateRange?.from?.hour() ?? 0,
      minute: dateRange?.from?.minute() ?? 0,
    }),
    [dateRange?.from],
  )
  const endTimeValue: TimeValue = useMemo(
    () => ({
      hour: dateRange?.to?.hour() ?? 23,
      minute: dateRange?.to?.minute() ?? 59,
    }),
    [dateRange?.to],
  )

  // Update local input values when props change
  useEffect(() => {
    setStartInputValue(startDateString)
  }, [startDateString])

  useEffect(() => {
    setEndInputValue(endDateString)
  }, [endDateString])

  // Combined validation and update logic
  const validateAndUpdateRange = (
    newStartValue: string,
    newEndValue: string,
    startTime?: TimeValue,
    endTime?: TimeValue,
  ) => {
    const parsedStart = newStartValue ? dayjs(newStartValue, dateFormat, true) : null
    const parsedEnd = newEndValue ? dayjs(newEndValue, dateFormat, true) : null

    // Validate start date format
    if (newStartValue && !parsedStart?.isValid()) {
      setError(
        t('date_picker.please_enter_a_valid_start_date_in_the_format', { format: dateFormat }),
      )
      return
    }

    // Validate end date format
    if (newEndValue && !parsedEnd?.isValid()) {
      setError(t('date_picker.please_enter_a_valid_end_date_in_the_format', { format: dateFormat }))
      return
    }

    // Combine date and time for datetime columns
    let finalStart = parsedStart
    let finalEnd = parsedEnd

    if (isDateTime && parsedStart && startTime) {
      finalStart = parsedStart.hour(startTime.hour).minute(startTime.minute).second(0)
    }

    if (isDateTime && parsedEnd && endTime) {
      finalEnd = parsedEnd.hour(endTime.hour).minute(endTime.minute).second(0)
    }

    // If both dates exist, validate range
    if (finalStart && finalEnd) {
      if (finalStart.isAfter(finalEnd)) {
        setError(t('date_picker.start_date_cannot_be_after_end_date'))
        return
      }

      // Valid range - update both
      setError(null)
      onRangeChange({
        from: finalStart,
        to: finalEnd,
      })
      return
    }

    // If only one date exists and is valid, update just that one
    if (finalStart) {
      setError(null)
      onRangeChange({
        from: finalStart,
      })
    } else if (finalEnd) {
      setError(null)
      onRangeChange({
        to: finalEnd,
      })
    }
  }

  // Skip when the input matches the prop — the change came from prop sync,
  // not user typing, and re-firing onRangeChange would feedback-loop.
  useDebounce(
    () => {
      if (startInputValue === startDateString && endInputValue === endDateString) {
        return
      }
      validateAndUpdateRange(startInputValue, endInputValue, startTimeValue, endTimeValue)
    },
    500,
    [startInputValue],
  )

  useDebounce(
    () => {
      if (startInputValue === startDateString && endInputValue === endDateString) {
        return
      }
      validateAndUpdateRange(startInputValue, endInputValue, startTimeValue, endTimeValue)
    },
    500,
    [endInputValue],
  )

  // Handle time changes for start date - immediately propagate via onRangeChange
  const handleStartTimeChange = useCallback(
    (type: 'hour' | 'minute', value: number) => {
      if (!dateRange?.from) {
        return
      }

      const newTimeValue = { ...startTimeValue, [type]: value }
      const finalStart = dateRange.from
        .hour(newTimeValue.hour)
        .minute(newTimeValue.minute)
        .second(0)
      // Preserve existing to date (with its current time)
      const finalEnd = dateRange.to

      if (finalEnd && finalStart.isAfter(finalEnd)) {
        setError(t('date_picker.start_date_cannot_be_after_end_date'))
        return
      }

      setError(null)
      onRangeChange({
        from: finalStart,
        to: finalEnd,
      })
    },
    [startTimeValue, dateRange, onRangeChange, setError, t],
  )

  // Handle time changes for end date - immediately propagate via onRangeChange
  const handleEndTimeChange = useCallback(
    (type: 'hour' | 'minute', value: number) => {
      if (!dateRange?.to) {
        return
      }

      const newTimeValue = { ...endTimeValue, [type]: value }
      const finalEnd = dateRange.to.hour(newTimeValue.hour).minute(newTimeValue.minute).second(0)
      // Preserve existing from date (with its current time)
      const finalStart = dateRange.from

      if (finalStart && finalStart.isAfter(finalEnd)) {
        setError(t('date_picker.start_date_cannot_be_after_end_date'))
        return
      }

      setError(null)
      onRangeChange({
        from: finalStart,
        to: finalEnd,
      })
    },
    [endTimeValue, dateRange, onRangeChange, setError, t],
  )

  // Handle arrow key navigation between inputs
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, inputType: 'start' | 'end') => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.stopPropagation()
    }

    if (inputType === 'start' && e.key === 'ArrowRight') {
      const start = e.currentTarget
      if (start.selectionStart === start.value.length) {
        endInputRef.current?.focus()
      }
    } else if (inputType === 'end' && e.key === 'ArrowLeft') {
      const end = e.currentTarget
      if (end.selectionStart === 0) {
        startInputRef.current?.focus()
      }
    }
  }

  // A range is partial if exactly one of from/to is set. Done is not enabled
  // unless the user completes or clears the range.
  const isPartialRange = Boolean(dateRange?.from) !== Boolean(dateRange?.to)

  return (
    <div className="flex flex-col gap-3 pt-4">
      <div className="flex flex-col gap-1 px-4">
        {isDateTime ? (
          // DateTime layout: vertical with dates and times paired
          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-3">
              {/* Start date and time */}
              <div className="flex flex-1 flex-col gap-2">
                <div className="text-xs font-medium text-gray-11">{t('date_picker.from')}</div>
                <div className="relative">
                  <Input
                    ref={startInputRef}
                    className="w-full text-xs"
                    disabled={disabled}
                    onBlur={() => setFocused(null)}
                    onChange={(e) => setStartInputValue(e.target.value)}
                    onFocus={() => setFocused('start')}
                    onKeyDown={(e) => handleKeyDown(e, 'start')}
                    placeholder={dateFormat}
                    type="text"
                    value={startInputValue}
                  />
                  <CalendarIcon className="pointer-events-none absolute end-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-10" />
                </div>
                <TimePicker
                  hour={startTimeValue.hour}
                  minute={startTimeValue.minute}
                  onHourChange={(value) => handleStartTimeChange('hour', value)}
                  onMinuteChange={(value) => handleStartTimeChange('minute', value)}
                />
              </div>

              {/* Arrow */}
              <div className="flex items-center pt-[34px]">
                {orientation === ApplicationOrientation.RIGHT_TO_LEFT ? (
                  <ArrowLeft className="h-4 w-4 text-gray-10" />
                ) : (
                  <ArrowRight className="h-4 w-4 text-gray-10" />
                )}
              </div>

              {/* End date and time */}
              <div className="flex flex-1 flex-col gap-2">
                <div className="text-xs font-medium text-gray-11">{t('date_picker.to')}</div>
                <div className="relative">
                  <Input
                    ref={endInputRef}
                    className="w-full text-xs"
                    disabled={disabled}
                    onBlur={() => setFocused(null)}
                    onChange={(e) => setEndInputValue(e.target.value)}
                    onFocus={() => setFocused('end')}
                    onKeyDown={(e) => handleKeyDown(e, 'end')}
                    placeholder={dateFormat}
                    type="text"
                    value={endInputValue}
                  />
                  <CalendarIcon className="pointer-events-none absolute end-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-10" />
                </div>
                <TimePicker
                  hour={endTimeValue.hour}
                  minute={endTimeValue.minute}
                  onHourChange={(value) => handleEndTimeChange('hour', value)}
                  onMinuteChange={(value) => handleEndTimeChange('minute', value)}
                />
              </div>
            </div>
            {error && (
              <div className="max-w-full">
                <span className="break-words text-xs text-red-9">{error}</span>
              </div>
            )}
          </div>
        ) : (
          // Date-only layout: horizontal inline
          <>
            <div
              className={cn(
                'flex relative w-full items-center rounded border border-gray-4 text-gray-10',
                focused && 'border-blue-8 shadow-input-focus outline-none',
                !focused && 'hover:border-gray-6',
              )}
            >
              <Input
                ref={startInputRef}
                className={cn(
                  'text-xs w-[90px]',
                  !startInputValue && 'w-[100px]',
                  focused === 'end' && 'opacity-50',
                )}
                containerClassName="pe-1 w-min"
                disabled={disabled}
                isBorderless
                onBlur={() => setFocused(null)}
                onChange={(e) => setStartInputValue(e.target.value)}
                onFocus={() => setFocused('start')}
                onKeyDown={(e) => handleKeyDown(e, 'start')}
                placeholder={dateFormat}
                type="text"
                value={startInputValue}
              />

              {orientation === ApplicationOrientation.RIGHT_TO_LEFT ? (
                <ArrowLeft className="-ml-1 -mr-1 h-3 w-3 flex-shrink-0" />
              ) : (
                <ArrowRight className="-ml-1 -mr-1 h-3 w-3 flex-shrink-0" />
              )}
              <Input
                ref={endInputRef}
                className={cn(
                  'text-xs w-[90px]',
                  !endInputValue && 'w-[100px]',
                  focused === 'start' && 'opacity-50',
                )}
                containerClassName="ps-1 w-min"
                disabled={disabled}
                isBorderless
                onBlur={() => setFocused(null)}
                onChange={(e) => setEndInputValue(e.target.value)}
                onFocus={() => setFocused('end')}
                onKeyDown={(e) => handleKeyDown(e, 'end')}
                placeholder={dateFormat}
                type="text"
                value={endInputValue}
              />
              <CalendarIcon className="absolute end-3 top-1/2 h-3.5 w-3.5 flex-shrink-0 -translate-y-1/2 transform text-gray-10" />
            </div>
            {error && (
              <div className="max-w-[220px]">
                <span className="break-words text-xs text-red-9">{error}</span>
              </div>
            )}
          </>
        )}
      </div>
      <div className="flex flex-row items-start justify-center border-b border-t border-gray-4">
        <Calendar
          className="px-4"
          initialFocus
          mode="range"
          month={currentMonth.toDate()}
          onMonthChange={onMonthChange}
          onSelect={(range) => {
            // Preserve time values when selecting dates from calendar
            let fromDate = range?.from ? dayjs(range.from) : undefined
            let toDate = range?.to ? dayjs(range.to) : undefined

            if (isDateTime) {
              if (fromDate) {
                fromDate = fromDate
                  .hour(startTimeValue.hour)
                  .minute(startTimeValue.minute)
                  .second(0)
              }
              if (toDate) {
                toDate = toDate.hour(endTimeValue.hour).minute(endTimeValue.minute).second(0)
              }
            }

            onRangeChange({
              from: fromDate,
              to: toDate,
            })
          }}
          selected={{
            from: dateRange?.from?.toDate(),
            to: dateRange?.to?.toDate(),
          }}
        />
      </div>
      <div className="flex justify-end gap-2 p-4 pt-0">
        <Button
          className="rounded"
          disabled={!!error || isPartialRange}
          onClick={onDoneClick}
          size="xs"
          type="button"
          variant="primary"
        >
          Done
        </Button>
      </div>
    </div>
  )
}
